/**
 * Design constants, lifted from the prototype so the rebuilt screens match it.
 *
 * The prototype styles everything with `var(--*)` from the design system's
 * token files, and this app does the same — which is what makes the dark-mode
 * toggle a single `data-theme` attribute rather than a second palette. The
 * literals below are only for the handful of values the prototype hard-coded
 * outside the token set.
 *
 * Metrics come from the prototype's inline styles, not from the token file,
 * wherever the two disagree. Two known disagreements:
 *   · the collapsed rail is 76px in the prototype and 56px in `--app-rail`
 *   · `CLOUD-PLAN.md` also says 56px
 * The prototype is the pixel reference, so 76 wins. Noted in docs/BACKLOG.md.
 */

import type React from 'react';

export const EASE = 'cubic-bezier(.22,.61,.36,1)';
export const HAIR = '1px solid var(--border-subtle)';

/** The few literals the prototype used outside the token set. */
export const C = {
  /** Avatar initials chip — the one colour pair with no semantic token. */
  avatarBg: '#e2d2e6',
  avatarInk: '#5b4063'
} as const;

/**
 * App chrome, decided 2026-09-09 — the readme's § "App chrome" section.
 *
 * The window ground is violet-tinted, the sidebar is a panel of its own on it
 * one step lighter and still tinted, and every content region is a *neutral*
 * panel at 16px radius with 16px of ground showing between. Cards on a panel
 * stay `--surface-card`.
 *
 * **Render from the `var()` names, not from these numbers.** The literals are
 * here so the decision is readable in TypeScript and so a value can be checked
 * against the token file without opening it — they are a mirror of the
 * `--app-*` block at the end of each theme in
 * `web/public/_ds/mauvely/tokens/colors.css`, which is the only definition the
 * browser ever sees. Anything that hardcodes one of these hexes will not follow
 * the theme, because nothing here switches on `data-theme`.
 */
export const APP = {
  light: {
    ground: '#e8e5f9',
    chromePanel: '#f5f3ff',
    /** slate-50 */
    panel: '#f8f9fd',
    /** slate-100 — strips inside a panel: a toolbar, a tab strip. */
    panelAlt: '#f3f5fe',
    /** Inputs, still reading as inset now the panel under them is lighter. */
    panelSunken: '#eef0f8'
  },
  dark: {
    /** indigo-950 */
    ground: '#12142e',
    chromePanel: '#1b1a33',
    panel: '#1b1d2b',
    panelAlt: '#1f2131',
    panelSunken: '#161826'
  }
} as const;

export const LAYOUT = {
  sidebar: 264,
  rail: 76,
  topbar: 60,
  /** Content padding: `30px 32px 40px` in the prototype. */
  contentPad: '30px 32px 40px',
  stackGap: 24,
  gridGap: 16,
  /** The ground showing between panels — `--gap-grid`, and the same 16 the
   *  native apps use. Also the inset of the sidebar panel from the window. */
  gutter: 16
} as const;

export const RADIUS = {
  panel: 22,
  card: 16,
  control: 10,
  pill: 999,
  /** `--radius-xl`. A chrome panel on the app ground — sidebar, content. */
  appPanel: 16
} as const;

/**
 * Motion, from the readme's § Motion. Milliseconds, matching `motion.css`.
 *
 * Anything that can be written in CSS is: `--transition-control`, `.mv-page`
 * and `.mv-pop` in `styles.css` carry the shapes, and `prefers-reduced-motion`
 * is handled there in one place. These are for the handful of transitions that
 * have to be inline because the value is computed in React.
 */
export const DUR = {
  /** Hover and press colour. */
  fast: 120,
  /** Reveals, tab switches, the nav marker sliding between rows. */
  base: 180,
  /** Dialogs and drawers. */
  slow: 280
} as const;

export const SHADOW = {
  hairline: '0 1px 0 rgba(40,28,45,.04)',
  md: '0 4px 14px rgba(40,28,45,.08)',
  lg: '0 18px 40px rgba(40,28,45,.14)',
  xl: '0 32px 70px rgba(40,28,45,.2)',
  popover: '0 18px 44px rgba(40,28,45,.18)'
} as const;

export const SCRIM = 'rgba(40,28,45,.44)';

/** Baloo 2 800. Titles in the Mauvely voice end in a full stop. */
export const display = (size: number): React.CSSProperties => ({
  fontFamily: "var(--font-display), 'Baloo 2', system-ui",
  fontWeight: 800,
  fontSize: size,
  lineHeight: size >= 32 ? 1.04 : 1.1,
  letterSpacing: '-.015em',
  color: 'var(--text-strong)',
  margin: 0
});

/** Eyebrow labels: mono, wide tracking, uppercase. */
export const mono: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 10.5,
  letterSpacing: '.17em',
  textTransform: 'uppercase',
  color: 'var(--text-faint)'
};

/** Metadata lines: mono, joined with `·`, never commas and never emoji. */
export const monoPlain: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 11,
  color: 'var(--text-muted)'
};

/** Table column headers. */
export const tableHead: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 10,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--text-quiet)'
};

export const panel: React.CSSProperties = {
  background: 'var(--surface-card)',
  borderRadius: RADIUS.panel,
  boxShadow: SHADOW.hairline
};

/**
 * Colour for the storage line, from usage.
 *
 * Thresholds are the prototype's: amber at 75%, red at 90%. The same figure
 * also switches the sidebar's call to action from "Get more space" to
 * "Free up space", which is why this returns the pair together.
 */
export function storageTone(percent: number): { color: string; action: string } {
  if (percent >= 90) return { color: 'var(--danger-500)', action: 'Free up space' };
  if (percent >= 75) return { color: 'var(--warning-500)', action: 'Free up space' };
  return { color: 'var(--signal)', action: 'Get more space' };
}

/**
 * Tint pair for a file-type tile.
 *
 * The prototype colours by source app: Compose violet, Snap mauve, Plan teal,
 * everything else neutral. Cloud cannot see which app a file came from yet —
 * per-app syncing is a later session — so this keys off the decrypted MIME
 * type instead and keeps the same palette.
 */
export function tint(mime: string | null): { bg: string; fg: string } {
  if (!mime) return { bg: 'var(--surface-sunken)', fg: 'var(--text-muted)' };
  if (mime.startsWith('image/')) return { bg: 'var(--accent-soft)', fg: 'var(--accent)' };
  if (mime.startsWith('video/') || mime.startsWith('audio/')) {
    return { bg: 'var(--signal-soft)', fg: 'var(--teal-700)' };
  }
  if (mime === 'application/pdf' || mime.startsWith('text/')) {
    return { bg: 'var(--primary-soft)', fg: 'var(--primary-text)' };
  }
  return { bg: 'var(--surface-sunken)', fg: 'var(--text-muted)' };
}

/** Lucide name for a file, from its decrypted type. */
export function fileIcon(kind: 'folder' | 'file', mime: string | null): string {
  if (kind === 'folder') return 'folder';
  if (!mime) return 'file-text';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/zip' || mime.includes('compressed')) return 'file-archive';
  return 'file-text';
}

// ── formatting ──────────────────────────────────────────────────────────────

/** Bytes as the design writes them: "8.4 MB", "2.4 GB", "—" for a folder. */
export function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/**
 * Relative for the last week, then an absolute short date — the pattern the
 * prototype uses throughout ("2 minutes ago", "Tuesday", "12 Jul").
 */
export function formatWhen(unixSeconds: number): string {
  const then = new Date(unixSeconds * 1000);
  const diff = Date.now() / 1000 - unixSeconds;

  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  if (diff < 172800) return 'yesterday';
  if (diff < 604800) return then.toLocaleDateString('en-GB', { weekday: 'long' });
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** "AR" for "Ada Rowe", falling back to the address. */
export function initials(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  if (parts.length === 1 && parts[0]!.length >= 2) return parts[0]!.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}
